import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, timer, of, throwError } from 'rxjs';
import { switchMap, tap, catchError, take, timeout } from 'rxjs/operators';
import { SocketService } from '../../event/services/socket.service';
import { DatadogService } from '../../observability/datadog.service';
import {
  ConnectionConfig,
  ConnectionLogger,
} from '../models/connection-state.model';
import { environment } from '../../../../environments/environment';

/**
 * Service responsável por gerenciar a reconexão automática do WebSocket
 * 
 * Responsabilidades:
 * - Tentar reconectar automaticamente após desconexão
 * - Implementar backoff exponencial com jitter
 * - Emitir número de tentativas
 * - Verificar REALMENTE se reconectou
 * 
 * Princípios SOLID:
 * - SRP: Responsabilidade única de reconexão
 * - OCP: Configurável através de ConnectionConfig
 * - DIP: Depende de abstração (SocketService)
 * 
 * MELHORIAS:
 * ✅ Verificação real de conexão (aguarda evento 'connect')
 * ✅ Backoff exponencial com jitter (±20%)
 * ✅ Logs configuráveis
 * ✅ Timeout de verificação de conexão
 */
@Injectable({
  providedIn: 'root',
})
export class ReconnectionService {
  private readonly config: ConnectionConfig = environment.connection;
  
  private readonly logger: ConnectionLogger;
  
  private reconnectionAttemptsSubject = new BehaviorSubject<number>(0);
  private isReconnectingSubject = new BehaviorSubject<boolean>(false);
  
  /**
   * Observable do número de tentativas de reconexão
   */
  public readonly reconnectionAttempts$: Observable<number> =
    this.reconnectionAttemptsSubject.asObservable();
  
  /**
   * Observable indicando se está tentando reconectar
   */
  public readonly isReconnecting$: Observable<boolean> =
    this.isReconnectingSubject.asObservable();

  constructor(
    private socketService: SocketService,
    private datadogService: DatadogService
  ) {
    this.logger = new ConnectionLogger(this.config.enableDebugLogs, this.datadogService);
  }

  /**
   * Tenta reconectar com backoff exponencial e jitter
   * 
   * @returns Observable<boolean> - true se reconectou, false se falhou
   */
  attemptReconnection(): Observable<boolean> {
    const currentAttempt = this.reconnectionAttemptsSubject.value + 1;
    
    this.reconnectionAttemptsSubject.next(currentAttempt);
    this.isReconnectingSubject.next(true);

    // Calcular delay com backoff exponencial + jitter
    const delay = this.calculateBackoffDelayWithJitter(currentAttempt);
    
    this.logger.log(
      `🔄 Tentativa de reconexão #${currentAttempt} em ${delay}ms...`
    );

    return timer(delay).pipe(
      take(1),
      switchMap(() => {
        // Tentar reconectar e REALMENTE verificar
        return this.tryReconnectAndVerify();
      }),
      tap({
        next: (success) => {
          if (success) {
            this.logger.log(`✅ Reconexão bem-sucedida na tentativa #${currentAttempt}`);
            this.resetAttempts();
          } else {
            this.logger.warn(`❌ Tentativa #${currentAttempt} falhou`);
          }
        },
        error: (error) => {
          this.logger.error(`❌ Erro na tentativa #${currentAttempt}:`, error);
        },
        finalize: () => {
          this.isReconnectingSubject.next(false);
        }
      }),
      catchError((error) => {
        this.isReconnectingSubject.next(false);
        // Retornar false ao invés de propagar erro
        return of(false);
      })
    );
  }

  /**
   * Reseta o contador de tentativas
   */
  resetAttempts(): void {
    this.reconnectionAttemptsSubject.next(0);
    this.isReconnectingSubject.next(false);
  }

  /**
   * Obtém o número atual de tentativas
   */
  getCurrentAttempts(): number {
    return this.reconnectionAttemptsSubject.value;
  }

  /**
   * Calcula o delay com backoff exponencial + jitter aleatório (±20%)
   * 
   * Jitter evita "thundering herd" quando múltiplos clientes desconectam juntos
   */
  private calculateBackoffDelayWithJitter(attempt: number): number {
    const exponentialDelay =
      this.config.reconnectBaseDelay *
      Math.pow(this.config.reconnectMultiplier, attempt - 1);

    // Limitar ao máximo configurado
    const cappedDelay = Math.min(exponentialDelay, this.config.reconnectMaxDelay);

    // Adicionar jitter de ±20%
    const jitterRange = cappedDelay * 0.2;
    const jitter = (Math.random() * 2 - 1) * jitterRange; // -20% a +20%

    return Math.max(0, Math.floor(cappedDelay + jitter));
  }

  /**
   * Tenta reconectar o WebSocket e VERIFICA se realmente conectou
   * 
   * CORREÇÃO: Não assume que conectou, aguarda evento real
   */
  private tryReconnectAndVerify(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      try {
        // Verificar se já está conectado
        if (this.socketService.isConnected()) {
          this.logger.log('✅ Já está conectado');
          observer.next(true);
          observer.complete();
          return;
        }

        // Listener para evento de conexão bem-sucedida
        const connectSubscription = this.socketService.onConnected$
          .pipe(
            take(1),
            timeout(5000) // 5s timeout para conectar
          )
          .subscribe({
            next: () => {
              // Conectou com sucesso!
              observer.next(true);
              observer.complete();
            },
            error: (error) => {
              // Timeout ou erro
              this.logger.warn('❌ Timeout ao aguardar conexão');
              observer.next(false);
              observer.complete();
            },
          });

        // Desconectar antes de reconectar (limpar estado)
        this.socketService.disconnect();
        
        // Aguardar um pouco antes de reconectar
        setTimeout(() => {
          this.logger.log('🔌 Tentando conectar...');
          this.socketService.connect();
        }, 100);

        // Cleanup quando observable for cancelado
        return () => {
          connectSubscription.unsubscribe();
        };
      } catch (error) {
        this.logger.error('❌ Erro ao tentar reconectar', { error });
        observer.next(false);
        observer.complete();
        return () => {}; // Return empty cleanup function
      }
    });
  }
}
